import { useCallback, useEffect, useRef, useState } from 'react';
import { useAudioRecorder } from 'react-audio-voice-recorder';

type RecordingState =
  | 'idle'
  | 'recording'
  | 'paused'
  | 'stopped'
  | 'processing';

interface UseRecordingStateReturn {
  recordingState: RecordingState;
  recordedBlob: Blob | null;
  audioDuration: number;
  recordingTime: number;
  isRecording: boolean;
  isPaused: boolean;
  recordingBlob: Blob | undefined;
  startRecording: () => Promise<void>;
  pauseResume: () => void;
  stopRecording: () => void;
  recordNew: () => void;
  setProcessing: () => void;
  setRecordedBlob: (blob: Blob | null) => void;
  setAudioDuration: (duration: number) => void;
  micError: string | null;
  hasMicPermission: boolean | null; // null when unknown/not checked yet
  volumeLevel: number; // 0..1 linear amplitude estimate
  selectedDeviceId: string | null;
  setSelectedDeviceId: (deviceId: string) => void;
  getMicPermission: (
    preflightOnly?: boolean
  ) => Promise<{ ok: boolean; stream?: MediaStream }>;
  clearMicError: () => void;
}

export const useRecordingState = (): UseRecordingStateReturn => {
  const {
    startRecording: startAudioRecording,
    stopRecording: stopAudioRecording,
    recordingBlob,
    recordingTime,
    isRecording,
    isPaused,
    togglePauseResume,
    mediaRecorder,
  } = useAudioRecorder();

  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const [micError, setMicError] = useState<string | null>(null);
  const [hasMicPermission, setHasMicPermission] = useState<boolean | null>(
    null
  );
  const [volumeLevel, setVolumeLevel] = useState<number>(0);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);

  // Volume metering internals (refs to persist across renders)
  const meterRafRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const micSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const meterStreamRef = useRef<MediaStream | null>(null);
  const lastLevelRef = useRef<number>(0);

  const stopVolumeMeter = useCallback(() => {
    if (meterRafRef.current) {
      cancelAnimationFrame(meterRafRef.current);
      meterRafRef.current = null;
    }
    setVolumeLevel(0);
    lastLevelRef.current = 0;
    try {
      micSourceRef.current?.disconnect();
    } catch {
      // ignore
    }
    analyserRef.current = null;
    micSourceRef.current = null;
    if (audioCtxRef.current) {
      // Closing can be expensive; suspend instead
      audioCtxRef.current.suspend().catch(() => {});
    }
    if (meterStreamRef.current) {
      meterStreamRef.current.getTracks().forEach(t => t.stop());
      meterStreamRef.current = null;
    }
  }, []);

  const getMicPermission = useCallback(
    async (
      preflightOnly: boolean = false
    ): Promise<{ ok: boolean; stream?: MediaStream }> => {
      setMicError(null);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: selectedDeviceId
            ? { deviceId: { exact: selectedDeviceId } }
            : true,
        });
        setHasMicPermission(true);
        if (preflightOnly) {
          stream.getTracks().forEach(t => t.stop());
          return { ok: true };
        }
        return { ok: true, stream };
      } catch (err: unknown) {
        const e = err as { name?: string } | undefined;
        let message =
          'Unable to access microphone. Please check your device and permissions.';
        if (e?.name === 'NotAllowedError') {
          message =
            "Microphone access was denied. Click the microphone icon in your browser's address bar to allow access, then try again.";
        } else if (e?.name === 'NotFoundError') {
          message =
            'No microphone found. Please connect a microphone and try again.';
        }
        setMicError(message);
        setHasMicPermission(false);
        return { ok: false };
      }
    },
    [selectedDeviceId]
  );

  const clearMicError = useCallback(() => setMicError(null), []);

  const startVolumeMeter = useCallback((stream: MediaStream) => {
    try {
      const Ctx =
        (
          window as unknown as {
            AudioContext?: typeof AudioContext;
            webkitAudioContext?: typeof AudioContext;
          }
        ).AudioContext ||
        (
          window as unknown as {
            AudioContext?: typeof AudioContext;
            webkitAudioContext?: typeof AudioContext;
          }
        ).webkitAudioContext;
      if (!Ctx) return; // environment without WebAudio
      audioCtxRef.current = audioCtxRef.current ?? new Ctx();
      const ctx = audioCtxRef.current;

      // Resume context if it was suspended
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      const analyser = ctx.createAnalyser();
      // Use a moderate fftSize for stable RMS estimation
      analyser.fftSize = 1024;
      // Note: smoothingTimeConstant affects primarily frequency data
      analyser.smoothingTimeConstant = 0.2;
      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
      analyserRef.current = analyser;
      micSourceRef.current = source;
      meterStreamRef.current = stream;

      const bufferLength = analyser.fftSize;
      const floatArray = new Float32Array(bufferLength);
      const byteArray = new Uint8Array(bufferLength);

      const tick = () => {
        const a = analyserRef.current;
        if (!a) return;
        // Prefer float time-domain data for accurate RMS; fallback to byte data
        let level = 0;
        try {
          a.getFloatTimeDomainData(floatArray);
          let sum = 0;
          for (let i = 0; i < bufferLength; i++) {
            const s = floatArray[i] || 0; // range ~[-1,1]
            sum += s * s;
          }
          const rms = Math.sqrt(sum / bufferLength);
          // Convert to dB FS, clamp to [-60, 0] dB, then normalize 0..1
          const db = 20 * Math.log10(rms || 1e-8);
          level = Math.min(1, Math.max(0, (db + 60) / 60));
        } catch {
          a.getByteTimeDomainData(byteArray);
          let max = 0;
          for (let i = 0; i < bufferLength; i++) {
            const v = Math.abs((byteArray[i] ?? 128) - 128) / 128; // approx amplitude 0..1
            if (v > max) max = v;
          }
          level = Math.min(1, Math.max(0, max));
        }

        // Apply a modest visual gain to keep normal speech responsive but avoid maxing out
        const VISUAL_GAIN = 1.1;
        const boosted = Math.min(1, Math.max(0, level * VISUAL_GAIN));

        // Silence gate: if below threshold, instantly drop to 0 to avoid a lingering single bar
        const SILENCE_THRESHOLD = 0.06; // keep fast drop on silence
        if (boosted < SILENCE_THRESHOLD) {
          lastLevelRef.current = 0;
          setVolumeLevel(0);
          meterRafRef.current = requestAnimationFrame(tick);
          return;
        }

        // Attack/Release smoothing to avoid flicker
        const prev = lastLevelRef.current || 0;
        const attack = 0.25; // slightly slower than original 0.35
        const release = 0.85; // fall slower
        const coeff = boosted > prev ? attack : release;
        const smoothed = prev + (boosted - prev) * coeff;
        lastLevelRef.current = smoothed;
        setVolumeLevel(smoothed);
        meterRafRef.current = requestAnimationFrame(tick);
      };
      meterRafRef.current = requestAnimationFrame(tick);
    } catch {
      // If metering fails, ignore silently
    }
  }, []);

  // Backward compat internal helper for start flow
  const obtainStreamForRecording =
    useCallback(async (): Promise<MediaStream | null> => {
      const res = await getMicPermission(false);
      return res.ok ? (res.stream ?? null) : null;
    }, [getMicPermission]);

  const startRecording = useCallback(async () => {
    if (recordingState === 'idle' || recordingState === 'stopped') {
      const stream = await obtainStreamForRecording();
      if (!stream) {
        // Keep state idle and surface error
        setRecordingState('idle');
        return;
      }
      // Start a volume meter on our own stream; the recorder hook uses its own stream
      startVolumeMeter(stream);
      setRecordingState('recording');
      startAudioRecording();
    }
  }, [
    recordingState,
    obtainStreamForRecording,
    startAudioRecording,
    startVolumeMeter,
  ]);

  const pauseResume = useCallback(() => {
    if (isRecording && !isPaused) {
      setRecordingState('paused');
      togglePauseResume();
      stopVolumeMeter();
    } else if (isPaused) {
      setRecordingState('recording');
      togglePauseResume();
      // Try to resume metering by requesting a fresh stream (previous one likely stopped)
      navigator.mediaDevices
        .getUserMedia({
          audio: selectedDeviceId
            ? { deviceId: { exact: selectedDeviceId } }
            : true,
        })
        .then(stream => startVolumeMeter(stream))
        .catch(() => {});
    }
  }, [
    isRecording,
    isPaused,
    togglePauseResume,
    startVolumeMeter,
    stopVolumeMeter,
    selectedDeviceId,
  ]);

  const stopRecording = useCallback(() => {
    if (isRecording || isPaused) {
      setRecordingState('stopped');
      setAudioDuration(recordingTime);
      setVolumeLevel(0); // Reset volume level when stopping
      stopAudioRecording();
      stopVolumeMeter();

      // Stop all media tracks to remove recording indicator
      if (mediaRecorder?.stream) {
        mediaRecorder.stream.getTracks().forEach(track => track.stop());
      }
    }
  }, [
    isRecording,
    isPaused,
    recordingTime,
    stopAudioRecording,
    stopVolumeMeter,
    mediaRecorder,
  ]);

  const recordNew = useCallback(() => {
    setRecordingState('idle');
    setRecordedBlob(null);
    setAudioDuration(0);
    setMicError(null);
    setVolumeLevel(0); // Reset volume level for next recording
    stopVolumeMeter();
  }, [stopVolumeMeter]);

  const setProcessing = useCallback(() => {
    setRecordingState('processing');
  }, []);

  // Cleanup metering on unmount
  useEffect(() => {
    return () => {
      stopVolumeMeter();
    };
  }, [stopVolumeMeter]);

  return {
    recordingState,
    recordedBlob,
    audioDuration,
    recordingTime,
    isRecording,
    isPaused,
    recordingBlob,
    startRecording,
    pauseResume,
    stopRecording,
    recordNew,
    setProcessing,
    setRecordedBlob,
    setAudioDuration,
    micError,
    hasMicPermission,
    volumeLevel,
    selectedDeviceId,
    setSelectedDeviceId,
    getMicPermission,
    clearMicError,
  };
};
